import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
import { DomainSourceSchema } from '@terra/shared-schemas-orchestrator';
import {
  toPersistedDomainConfigList,
  type ApplicationRoleDto,
  type DomainConfigInputSource,
} from '@terra/shared-contracts-applications';
import { useApplicationDomains } from '../../../hooks/api/useApplicationDomains';
import { useApplicationRoles } from '../../../hooks/api/useApplicationRoles';
import { useApplicationGitIntegrations } from '../../../hooks/api/useApplicationGitIntegrations';
import { useAiApplication } from '../../../hooks/api/useAiApplication';
import { useApplicationCopilotEditContextStore } from '../../../store/applicationCopilotEditContextStore';
import type { PortalTarget } from '../../../store/projectStore';
import { useTargetCredentialsState } from './useTargetCredentialsState';
import { useTargetSettingsMutation } from './useTargetSettingsMutation';

export function useTargetSettingsForm({
  selectedTarget,
  tenantId,
  appTargetId,
  isMcpServerTarget,
}: {
  selectedTarget: PortalTarget | undefined;
  tenantId?: string;
  appTargetId?: string;
  isMcpServerTarget: boolean;
}) {
  const applicationId = selectedTarget?.id;
  const [customRoles, setCustomRoles] = useState<ApplicationRoleDto[]>([]);
  const [domainConfigs, setDomainConfigs] = useState<DomainConfigInputSource[]>(
    [],
  );
  const [originalDomainConfigs, setOriginalDomainConfigs] = useState<
    DomainConfigInputSource[]
  >([]);
  const credentialsState = useTargetCredentialsState({
    applicationId,
    tenantId,
  });
  const { aiApplication: syncedAiApplication } = useAiApplication({
    aiApplicationId: appTargetId ?? null,
    enabled: isMcpServerTarget && Boolean(appTargetId),
  });
  const updateApplicationMutation = useTargetSettingsMutation({
    applicationId,
    tenantId,
  });
  const { data: domainsData, isLoading: domainsLoading } =
    useApplicationDomains({ applicationId, tenantId });
  const {
    data: rolesData,
    isLoading: rolesLoading,
    refetch: refetchRoles,
  } = useApplicationRoles({ applicationId, tenantId });
  const { data: gitIntegrationData } = useApplicationGitIntegrations({
    applicationId,
    tenantId,
  });

  useEffect(() => {
    if (domainsData?.domains) {
      const validDomains = toPersistedDomainConfigList({
        domains: domainsData.domains,
        previousDomains: domainsData.domains,
      });

      setDomainConfigs(validDomains);
      setOriginalDomainConfigs(validDomains);
    }
  }, [domainsData]);

  useEffect(() => {
    if (rolesData?.roles) {
      setCustomRoles(rolesData.roles);
    }
  }, [rolesData]);

  useEffect(() => {
    useApplicationCopilotEditContextStore.getState().setRoleCatalog(
      customRoles.map((role) => ({
        id: role.id,
        name: role.name,
        description: role.description,
      })),
    );
  }, [customRoles]);

  const applicationUrl =
    isMcpServerTarget && syncedAiApplication?.url
      ? syncedAiApplication.url
      : (selectedTarget?.url ?? '');

  const hasGitIntegration = useMemo(
    () =>
      (gitIntegrationData?.gitIntegrations ?? []).some(
        (integration) =>
          Array.isArray(integration.repositories) &&
          integration.repositories.length > 0,
      ),
    [gitIntegrationData],
  );
  const discoveredSubdomains = useMemo(
    () =>
      domainConfigs
        .filter(
          ({ source }) => source !== DomainSourceSchema.enum.USER_PROVIDED,
        )
        .map(({ domain }) => domain),
    [domainConfigs],
  );
  const domainScopeTableDomains = useMemo(
    () =>
      toPersistedDomainConfigList({
        domains: domainConfigs,
        previousDomains: originalDomainConfigs,
      }),
    [domainConfigs, originalDomainConfigs],
  );

  const originalDomainConfigsRef = useRef(originalDomainConfigs);

  originalDomainConfigsRef.current = originalDomainConfigs;
  const persistDomainConfigs = useCallback(
    (configsToSave: DomainConfigInputSource[]) => {
      if (!applicationId) {
        return;
      }

      const persistedDomains = toPersistedDomainConfigList({
        domains: configsToSave,
        previousDomains: originalDomainConfigsRef.current,
      });

      updateApplicationMutation.mutate(
        { domains: persistedDomains, skipDomainQueryInvalidation: true },
        { onSuccess: () => setOriginalDomainConfigs(persistedDomains) },
      );
    },
    [applicationId, updateApplicationMutation],
  );
  const debouncedPersistDomainConfigs = useDebounceCallback(
    persistDomainConfigs,
    500,
  );
  const handleDomainScopeTableChange = useCallback(
    (newDomainConfigs: DomainConfigInputSource[]) => {
      setDomainConfigs(newDomainConfigs);
      if (!applicationId) {
        return;
      }

      debouncedPersistDomainConfigs(newDomainConfigs);
    },
    [debouncedPersistDomainConfigs, applicationId],
  );

  return {
    ...credentialsState,
    customRoles,
    setCustomRoles,
    rolesLoading,
    refetchRoles,
    hasGitIntegration,
    domainsLoading,
    discoveredSubdomains,
    domainScopeTableDomains,
    handleDomainScopeTableChange,
    applicationUrl,
  };
}
